import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import isEqual from 'react-fast-compare';
import { useFormikContext } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';

import Placeholder from 'components/Placeholder';
import FA from 'utils/FA';
import { titleize, ensureKeyed } from '../../../utils';
import {
  reviewFields as reviewFieldsDuck,
  fields as fieldsDuck
} from '../../../ducks';
import { duckify, filterDuck } from '../../../utils/duckify';
import ReviewPage from './ReviewPage';
import './styles/CandidateCoverSheet.scss';

const getSectionValues = (section, values) => values.filter(field => field.section === section);

/**
 * If more sections are added in the future, add them to this sections array.
 * This array is used to generate each of the separate sortable lists.
 * */
const sections = ['demographic', 'key_information'];

const SortableListItem = SortableElement(({
  item,
  sortIndex,
  action,
  isSubmitting
}) => (
  <li className='review-sortable-list-item'>
    <div className='review-sortable-list-reorder'>
      <FA icon={faBars} nbsp />
    </div>
    <div className='review-sortable-list-cover-sheet-field-name'>
      {item.field_name}
    </div>
    <div className='review-sortable-list-cover-sheet-actions'>
      <Button
        variant='danger'
        size='xs'
        onClick={() => action(sortIndex)}
        disabled={isSubmitting}
      >
        <FA icon={faTimes} nbsp />
      </Button>
    </div>
  </li>
));

const SortableList = SortableContainer(({
  items,
  action,
  section,
  isSubmitting
}) => (
  <ul className='review-sortable-list'>
    {items.map((item, index) => (
      <SortableListItem
        key={item.id}
        index={index}
        sortIndex={index}
        item={item}
        action={action}
        disabled={isSubmitting}
        isSubmitting={isSubmitting}
      />
    )).map(ensureKeyed(`${section}_list_item`))}
  </ul>
));

const AddField = ({
  options,
  value,
  disabled,
  onConfirm,
  onCancel,
  onChange
}) => (
  <Form.Group className='review-settings-add-field-container'>
    <div className='review-settings-add-field-selector'>
      <Select
        options={options}
        value={value}
        onChange={onChange}
        labelKey='display_name'
        valueKey='id'
      />
    </div>
    <div className='review-settings-add-field-buttons'>
      <Button
        onClick={onConfirm}
        disabled={(!value && value !== '') || disabled}
      >
        <FA icon={faCheck} nbsp />
      </Button>
      <Button variant='light' onClick={onCancel}>
        <FA icon={faTimes} nbsp />
      </Button>
    </div>
  </Form.Group>
);

AddField.propTypes = {
  /** Select Options */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      id: PropTypes.string
    })
  ),

  /** Selects current value */
  value: PropTypes.shape({
    display_name: PropTypes.string,
    id: PropTypes.string
  }),

  /** Section is disabled */
  disabled: PropTypes.bool,

  /** Function called when confirm add field is selected */
  onConfirm: PropTypes.func,

  /** Function called when cancel add field is selected */
  onCancel: PropTypes.func,

  /** Function called when a new option is selected */
  onChange: PropTypes.func
};

AddField.defaultProps = {
  options: [],
  value: {},
  disabled: false,
  onConfirm: () => false,
  onCancel: () => false,
  onChange: () => false
};

const FieldList = ({
  section,
  fields,
  sectionFields,
  setItemPendingDeleteIndex,
  setShowConfirmDelete
}) => {
  const [showAddField, setShowAddField] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const { values, isSubmitting, setFieldValue } = useFormikContext();

  const onSortEnd = (oldIndex, newIndex) => {
    const items = values.reviewFields.filter(item => item.section === section);
    const oldReviewFieldsIndex = values.reviewFields.indexOf(items[oldIndex]);
    const newReviewFieldsIndex = values.reviewFields.indexOf(items[newIndex]);

    if (oldReviewFieldsIndex !== newReviewFieldsIndex) {
      setFieldValue(
        'reviewFields',
        arrayMove(values.reviewFields, oldReviewFieldsIndex, newReviewFieldsIndex)
      );
    }
  };

  return (
    <div className={`review-sub-section review-sub-section-${section}`}>
      <div className='review-sub-section-header'>{titleize(section)}</div>
      <div className='review-section-help-text'>
        Select fields to include under “
        {titleize(section)}
        ” on your Cover Sheet.
      </div>
      <div className={`review-${section}-list review-sortable-list-container`}>
        <div className='review-sortable-list-header-row'>
          <div className='review-sortable-list-cover-sheet-field-name review-sortable-list-header'>
            Field Name
          </div>
          <div className='review-sortable-list-cover-sheet-actions'>
            Actions
          </div>
        </div>
        <SortableList
          lockAxis='y'
          distance={10}
          lockToContainerEdges
          items={sectionFields}
          section={section}
          isSubmitting={isSubmitting}
          action={index => {
            setItemPendingDeleteIndex(values.reviewFields.indexOf(sectionFields[index]));
            setShowConfirmDelete(true);
          }}
          onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, section)}
        />
        <Button
          variant='link'
          className='review-settings-add-field'
          onClick={() => setShowAddField(!showAddField)}
          disabled={isSubmitting}
        >
          <FA icon={faPlusCircle} nbsp />
          Add another Field
        </Button>
        {showAddField && (
          <AddField
            options={fields}
            value={selectedField}
            disabled={isSubmitting}
            onConfirm={() => setFieldValue(
              'reviewFields',
              [
                ...values.reviewFields,
                {
                  section,
                  field_id: selectedField.id,
                  field_name: selectedField.display_name,
                  filter_option: false
                }
              ]
            )}
            onCancel={() => setShowAddField(false)}
            onChange={selected => setSelectedField(selected)}
          />
        )}
      </div>
    </div>
  );
};

FieldList.propTypes = {
  /** Section name (ie. demographic or key_information) */
  section: PropTypes.string,

  /** List of all selectable fields */
  fields: PropTypes.arrayOf(PropTypes.shape({})),

  /** Current sections selected fields */
  sectionFields: PropTypes.arrayOf(PropTypes.shape({})),

  /** Callback to set item pending delete in parent */
  setItemPendingDeleteIndex: PropTypes.func,

  /** Callback to show the confirm delete modal */
  setShowConfirmDelete: PropTypes.func
};

FieldList.defaultProps = {
  section: 'unnamed',
  fields: [],
  sectionFields: [],
  setItemPendingDeleteIndex: () => false,
  setShowConfirmDelete: () => false
};

const FieldListContainer = (props) => {
  const { values } = useFormikContext();

  return (
    <React.Fragment>
      {sections.map(section => (
        <FieldList
          key={section}
          section={section}
          sectionFields={getSectionValues(section, values.reviewFields)}
          {...props}
        />
      ))}
    </React.Fragment>
  );
};

const CandidateCoverSheet = ({
  className,
  fields,
  reviewFields,
  route,
  allDucksReady,
  schoolYearId,
  saveReviewFields,
  deleteReviewField,
  reloadDucks
}) => {
  const [formikValues, setFormikValues] = useState({ reviewFields });
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemPendingDeleteIndex, setItemPendingDeleteIndex] = useState();

  /** Initialize Formik state and update to ducks when year is changed */
  useEffect(() => {
    if (allDucksReady) {
      setFormikValues({ reviewFields });
    }
  }, [allDucksReady, schoolYearId]);

  const isSectionChanged = (section, values, initialValues) => (
    !isEqual(getSectionValues(section, values.reviewFields), getSectionValues(section, initialValues.reviewFields))
  );

  const onSubmit = ({ values, initialFormikValues }) => (
    Promise.all(sections
      .filter(section => isSectionChanged(section, values, initialFormikValues))
      .map(section => saveReviewFields({
        reviewFields: getSectionValues(section, values.reviewFields),
        id: schoolYearId
      })))
  );

  const handleDelete = ({ values, setFieldValue }) => {
    const { id } = values.reviewFields[itemPendingDeleteIndex];
    const array = [...values.reviewFields];

    array.splice(itemPendingDeleteIndex, 1);
    setShowConfirmDelete(false);

    /** If item needs to be deleted from server db send delete */
    if (id) {
      return (
        deleteReviewField({ id })
          .then(() => setFieldValue('reviewFields', array))
          .then(() => setFormikValues({
            reviewFields: array
          }))
      );
    }

    setFieldValue('reviewFields', array);
    return Promise.resolve({ skipFlash: true });
  };

  return (
    <ReviewPage
      className={className}
      header='Candidate Cover Sheet'
      headerHelperText='Designate which fields to display on the Cover Sheet of the Review tab on contact records.'
      initialFormikValues={formikValues}
      route={route}
      showConfirmDelete={showConfirmDelete}
      onClose={() => setShowConfirmDelete(false)}
      onConfirm={handleDelete}
      onSubmit={onSubmit}
      reloadDucks={reloadDucks}
    >
      <Placeholder.loading
        ready={allDucksReady}
        content={() => (
          <FieldListContainer
            fields={fields}
            setItemPendingDeleteIndex={setItemPendingDeleteIndex}
            setShowConfirmDelete={setShowConfirmDelete}
          />
        )}
      />
    </ReviewPage>
  );
};

CandidateCoverSheet.propTypes = {
  /** Classname prop */
  className: PropTypes.string,

  /** duck of all selectable fields */
  fields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    display_name: PropTypes.string,
    name: PropTypes.string
  })).isRequired,

  /** duck of fields used for review */
  reviewFields: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    field_id: PropTypes.string,
    field_name: PropTypes.string,
    section: PropTypes.string,
    filter_option: PropTypes.bool
  })).isRequired,

  /** React router route */
  route: PropTypes.shape({}).isRequired,

  /** All Ducks Ready */
  allDucksReady: PropTypes.bool.isRequired,

  /** Current Track Silo Selected School Id */
  schoolYearId: PropTypes.string.isRequired,

  /** Submit Ducks Function */
  saveReviewFields: PropTypes.func.isRequired,

  /** Remove Duck Function */
  deleteReviewField: PropTypes.func.isRequired,

  /** marks ducks dirty for reload */
  reloadDucks: PropTypes.func.isRequired
};

CandidateCoverSheet.defaultProps = {
  className: ''
};

const mapDucksToProps = props => {
  const { schoolYearId } = props;
  return {
    reviewFields: filterDuck(reviewFieldsDuck, { school_year_id: schoolYearId }),
    fields: filterDuck(fieldsDuck, { selectable: true })
  };
};

const mapDispatchToProps = dispatch => ({
  saveReviewFields: data => (
    dispatch(reviewFieldsDuck.actions.bulkUpdate(data.reviewFields, { school_year_id: data.id }))
      .then(reviewFieldsDuck.actions.denormalizeObjects)
  ),
  deleteReviewField: field => (
    dispatch(reviewFieldsDuck.actions.remove(field))
      .then(reviewFieldsDuck.actions.denormalizeObjects)
  ),
  reloadDucks: () => dispatch(reviewFieldsDuck.actions.markDirty())
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(CandidateCoverSheet);
